import { toBody } from "./sheetBody";
import { enqueue, getMeta, getOutboxOp, rides, scoreSheets, shipLog, tests } from "./db";

// Card #3334 -- "no scoresheet left behind". The scribe-fe half of the sheet
// manifest check: build the {id, version} list of every sheet THIS tablet
// shipped for the current show, and turn the ids the server says it lost (or
// holds an older version of) into resend ops queued from the tablet's LIVE
// local copy (never the station snapshot).
//
// Server contract (score-api, c0dd54184): POST /show/:showId/sheet-manifest
// takes the id+version list in ONE call (no fingerprint, no two-step) and
// answers { match, missing, stale }. A resend is a createScoreSheet with
// `resend: true, resendOf: <original id>, resendVersion: <local version>`;
// the server stores it as a DRAFT contender under a derived id, never takes
// the seat lock, never moves ring state, and counts it as satisfying the
// original id from then on. So the manifest KEEPS sending the original id
// and the current local version after a resend landed.

// score-api accepts at most this many sheets per manifest call.
export const MANIFEST_MAX_SHEETS = 2000;

export const resendOpId = (originalId) => `resend:${originalId}`;

// Meta key the engine stamps when a resend lands (sync.js applyResend):
// { version, contenderId }. Informational only (support / debugging); the
// server is the source of truth for "is this sheet present".
export const resentMetaKey = (originalId) => `resent:${originalId}`;

async function showIdOfSheet(row) {
  if (row.showId) return row.showId;
  const ride = row.rideId ? await rides.get(row.rideId).catch(() => null) : null;
  return ride?.showId ?? null;
}

// The ship log's server ids, narrowed to sheets this device still holds a
// real (non-provisional) local row for, in this show. A shipLog serverId with
// no local row (an evicted cache, or a previous resend's contender id) is
// skipped: there is nothing to resend from, so there is nothing to ask about.
export async function buildSheetManifest(showId) {
  const log = await shipLog.getAll().catch(() => []);
  const ids = new Set();
  for (const entry of log) {
    if (entry?.serverId) ids.add(entry.serverId);
  }
  const sheets = [];
  for (const id of ids) {
    const row = await scoreSheets.get(id).catch(() => null);
    if (!row || row.provisional) continue;
    if ((await showIdOfSheet(row)) !== showId) continue;
    sheets.push({ id, version: typeof row.version === "number" ? row.version : 0 });
    if (sheets.length >= MANIFEST_MAX_SHEETS) break;
  }
  return sheets;
}

// Builds and enqueues one resend op per id, from the live local row. Returns
// the opIds queued. Idempotent per original id: the opId is `resend:<id>`, so
// a repeat enqueue upserts the same outbox row (db.js enqueue).
export async function enqueueSheetResends(ids) {
  const queued = [];
  for (const id of new Set(ids ?? [])) {
    const row = await scoreSheets.get(id).catch(() => null);
    if (!row || row.provisional || !row.rideId || !row.judgeId) continue;
    const test = row.testId ? await tests.get(row.testId).catch(() => null) : null;
    const version = typeof row.version === "number" ? row.version : 0;
    // The server may keep reporting an id whose resend already landed (the
    // snapshot reply's missingSheets can lag). A resend recorded at THIS local
    // version is done: skip it, on the manifest path and the snapshot path.
    const landed = await getMeta(resentMetaKey(id)).catch(() => undefined);
    if (landed && landed.version === version) continue;
    // #3343, reshaped by card #3546 -- a resend op already queued for THIS
    // local version is left exactly as it is, whatever state it is in (pending,
    // backing off after a 4xx, ...). enqueue() upserts with status "pending"
    // and a cleared backoff, so re-enqueueing it on every manifest check or
    // snapshot reply would reset its retry schedule each time. The engine
    // retries it on its own; a NEW local version (a different resendVersion)
    // still re-enqueues, so fresher marks replace the stale body.
    const existingOp = await getOutboxOp(resendOpId(id)).catch(() => undefined);
    if (existingOp && existingOp.payload?.body?.resendVersion === version) continue;
    const body = {
      ...toBody(row, test, null),
      rideId: row.rideId,
      judgeId: row.judgeId,
      resend: true,
      resendOf: id,
      resendVersion: version,
    };
    await enqueue({
      opId: resendOpId(id),
      type: "createScoreSheet",
      entityKey: `${row.rideId}::${row.judgeId}`,
      payload: {
        rideId: row.rideId,
        judgeId: row.judgeId,
        serverId: null,
        resend: true,
        resendOf: id,
        body,
      },
    });
    queued.push(resendOpId(id));
  }
  return queued;
}

// One full check: manifest -> server -> enqueue a resend for each missing or
// stale id. `scoreApi` is useScoreApi()'s client. Throws on a failed call so
// the caller can back off. Returns { checked, resent }.
export async function runSheetManifestCheck(scoreApi, showId) {
  const sheets = await buildSheetManifest(showId);
  if (sheets.length === 0) return { checked: 0, resent: [] };
  const res = await scoreApi.stationSnapshot.checkSheetManifest(showId, { sheets });
  const data = res?.data?.data ?? {};
  const ids = [...(data.missing ?? []), ...(data.stale ?? [])];
  const resent = ids.length ? await enqueueSheetResends(ids) : [];
  return { checked: sheets.length, resent };
}
